import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import HeaderBar from "../components/HeaderBar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

/**
 * V2.0.19 — Public Terms of Service / Legal Disclaimer page.
 *
 * Mirrors the disclaimer surfaced inside the lightbox `I Accept` checkbox
 * but in long form, so a user clicking the footer link or the lightbox
 * "Read the full Terms" link lands here.
 *
 * V2.0.50 — Added "Digital Delivery & Refund Policy" with explicit
 *           "All Sales Are Final upon digital download delivery"
 *           clause so Stripe and other merchant processors can audit
 *           the policy.
 *
 * No auth required.
 */
export default function TermsOfService() {
  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 text-zinc-100" data-testid="terms-page">
      <SEO
        title="Terms of Service"
        description="Terms governing your use of Blueprint AI. Digital deliverables, all-sales-final policy, and limitation of liability for advisory architectural scans."
        path="/terms"
        themeColor="#0a0a0a"
      />
      <HeaderBar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 lg:px-10 py-16">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-brass-400 transition-colors mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          // back to home
        </Link>

        <h1
          data-testid="terms-page-title"
          className="font-mono text-3xl md:text-4xl text-brass-400 uppercase tracking-wide mb-3"
        >
          // Important Legal Disclaimer
        </h1>
        <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-12">
          Terms of Service · Effective February 2026
        </p>

        <Section title="1. No Professional Architectural or Engineering Services">
          <p>
            Blueprint AI LLC (&ldquo;the Company,&rdquo; &ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;) provides an automated,
            artificial-intelligence-driven software platform (&ldquo;the
            Platform&rdquo;) designed to generate conceptual residential
            building layouts, floor plans, and digital Building Information
            Modeling (BIM) files in Industry Foundation Classes (IFC) and
            Portable Document Format (PDF).
          </p>
          <p>
            Blueprint AI LLC is <strong className="text-zinc-100">not</strong>{" "}
            a licensed architect, professional engineer, or structural design
            firm. The Platform, its outputs, its automated code-checking
            features, and any content or consultations provided on or through
            the Platform do not constitute, and shall not be construed as,
            professional architectural, engineering, or construction services,
            advice, or documentation.
          </p>
        </Section>

        <Section title="2. Conceptual Nature of Deliverables">
          <p>
            All digital files, drawings, schemas, blueprints, metadata, and
            reports generated by the Platform (collectively,
            &ldquo;Deliverables&rdquo;) are strictly conceptual design
            concepts intended for exploratory, planning, and visualization
            purposes only.
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong className="text-zinc-100">
                Architectural Review and Stamping Required:
              </strong>{" "}
              The Deliverables are not finalized construction documents, are
              not approved for structural soundness, and are not suitable or
              legally valid for submittal to any local building department,
              zoning board, or municipality for the purposes of obtaining a
              building or construction permit.
            </li>
            <li>
              <strong className="text-zinc-100">
                The User&apos;s Mandatory Obligation:
              </strong>{" "}
              To convert the Deliverables into buildable, permittable
              construction documents, the User must retain a locally
              licensed, qualified professional architect, structural
              engineer, and/or general contractor to fully review, modify,
              verify, and apply their professional stamp and seal to the
              designs in accordance with regional jurisdiction laws.
            </li>
          </ul>
        </Section>

        <Section title="3. Local Building Codes, Zoning, and Ordinances">
          <p>
            While the Platform utilizes automated tools to cross-reference
            and pull localized residential building codes, zoning
            restrictions, setback rules, height limits, and municipal
            ordinances (collectively, &ldquo;Local Codes&rdquo;), the Company
            does not guarantee the accuracy, completeness, or timeliness of
            such data.
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              Local Codes are subject to frequent, unannounced amendments,
              variances, and localized interpretations by local building
              officials (Authorities Having Jurisdiction, or AHJ).
            </li>
            <li>
              The User and the User&apos;s licensed professional carry sole,
              exclusive responsibility for verifying that the final site
              plan, building dimensions, roof pitches, structural loading
              capacities, and setbacks comply with all active local
              municipal, county, state, and federal regulations.
            </li>
            <li>
              The Company shall not be held liable for any zoning violations,
              permit denials, setbacks disputes, or project delays arising
              from inaccuracies or omissions within the Platform&apos;s
              automated code-checking engine.
            </li>
          </ul>
        </Section>

        <Section title="5. Digital Delivery & Refund Policy">
          <p>
            All Deliverables generated by the Platform — including but not
            limited to IFC files, Architectural PDFs, AI photo-realistic
            renders, and Project Unlock tiers — are digital goods delivered
            immediately upon successful payment.
          </p>
          <p>
            <strong className="text-zinc-100">
              ALL SALES ARE FINAL UPON DIGITAL DOWNLOAD DELIVERY.
            </strong>{" "}
            Because Deliverables are non-revocable digital assets that can
            be downloaded, copied, and integrated into other workflows the
            moment the purchase completes, the Company does not offer
            refunds, exchanges, or chargebacks for any digital purchase.
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              The User acknowledges that by clicking the &ldquo;Pay&rdquo;
              button on the Stripe-hosted checkout page, the User is
              expressly waiving the right to a refund and is consenting to
              immediate delivery of the digital Deliverable.
            </li>
            <li>
              If a charge is disputed with the User&apos;s card issuer or
              bank after the Deliverable has been successfully delivered,
              the Company reserves the right to (a) provide its merchant
              processor with the audit trail of the delivered file, and
              (b) suspend or terminate the User&apos;s account.
            </li>
            <li>
              In the rare event of a verified technical failure that
              prevented the Deliverable from being downloaded, the User may
              contact{" "}
              <a
                href="mailto:jonathan@blueprintaillc.com"
                className="text-brass-400 hover:underline"
              >
                jonathan@blueprintaillc.com
              </a>{" "}
              within 7 days of the transaction. The Company&apos;s sole
              remedy is to re-deliver the file or to extend an equivalent
              credit at its discretion.
            </li>
          </ul>
        </Section>

        <Section title="6. Limitation of Liability and Indemnification">
          <p>
            To the maximum extent permitted by applicable law, in no event
            shall Blueprint AI LLC, its founders, officers, employees, or
            platform infrastructure partners (including Emergent) be liable
            for any direct, indirect, incidental, special, consequential, or
            punitive damages. This includes, without limitation, financial
            losses, loss of profits, cost of alternative drafting services,
            structural or construction failures, or delays resulting from:
          </p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Your use of, or inability to use, the Platform or its Deliverables;</li>
            <li>Any errors, inaccuracies, or bugs in the generated IFC or PDF files;</li>
            <li>
              Any reliance placed on the completeness or legal compliance of
              the conceptual files.
            </li>
          </ol>
          <p>
            By downloading and purchasing the Deliverables, the User agrees
            to defend, indemnify, and hold harmless Blueprint AI LLC from and
            against any and all claims, damages, obligations, losses,
            liabilities, costs, or debt (including but not limited to
            attorney&apos;s fees) arising from the construction, modification,
            or structural execution of any building or project modeled using
            the Platform&apos;s outputs.
          </p>
        </Section>

        <div className="mt-16 border-t border-zinc-800 pt-8 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          © 2026 Blueprint AI LLC · All rights reserved
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-mono text-base md:text-lg text-zinc-100 uppercase tracking-wide mb-5 border-l-2 border-brass-400 pl-3">
        {title}
      </h2>
      <div className="space-y-4 text-sm md:text-base leading-relaxed text-zinc-300">
        {children}
      </div>
    </section>
  );
}
